import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  Camera,
  Activity,
  Heart,
  Brain,
  Clock,
  Pill,
  Target,
  LineChart,
  Utensils,
  Moon,
  Zap,
  Shield,
  Watch,
  Smartphone,
  Database,
} from "lucide-react";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import LandingNav from "@/projects/Admin_Bite_Balance/bite-balance/src/components/landing/LandingNav";
import LandingFooter from "@/projects/Admin_Bite_Balance/bite-balance/src/components/landing/LandingFooter";

// Hand-drawn circle SVG for "AI"
const HandDrawnCircle = () => (
  <svg
    className="absolute -inset-2 h-[calc(100%+16px)] w-[calc(100%+16px)]"
    viewBox="0 0 100 60"
    preserveAspectRatio="none"
  >
    <ellipse
      cx="50"
      cy="30"
      rx="45"
      ry="25"
      fill="none"
      stroke="#22c55e"
      strokeWidth="2.5"
      strokeLinecap="round"
      className="animate-draw-circle"
      style={{
        strokeDasharray: 300,
        strokeDashoffset: 300,
      }}
    />
  </svg>
);

// Hand-drawn brush stroke highlight for "Gut"
const BrushHighlight = () => (
  <span className="animate-fade-in-highlight absolute -bottom-1 left-0 h-3 w-full -skew-x-6 rounded bg-green-400/50" />
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [taglineText, setTaglineText] = useState("");
  const fullTagline = "Balance food, boost your mood";

  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullTagline.length) {
        setTaglineText(fullTagline.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 50);
    return () => clearInterval(timer);
  }, []);

  const coreFeatures = [
    {
      icon: Camera,
      title: "AI Meal Scanning",
      description:
        "Snap a photo of any meal and our OpenAI Vision-powered system instantly identifies food items, estimates portions, and calculates complete nutritional data including macros, GI, and GL.",
    },
    {
      icon: Heart,
      title: "Bite Balance Score",
      description:
        "Get a comprehensive 0-100 score combining glycemic load, body composition, and activity data into a single daily health metric with clear status ranges.",
    },
    {
      icon: Zap,
      title: "Glucose Spike Prediction",
      description:
        "Every meal you log gets an instant blood glucose spike prediction—High, Medium, or Low—with confidence scores, time-to-peak, and recovery estimates.",
    },
    {
      icon: Activity,
      title: "Fitbit Integration",
      description:
        "Seamlessly sync your Fitbit data including steps, active minutes, calories burned, sleep duration, heart rate, HRV, SpO₂, and skin temperature.",
    },
    {
      icon: Brain,
      title: "Health Insights",
      description:
        "Understand your carb-fiber balance, fat quality, sugar load, and metabolic stability with clear, actionable insights derived from your daily data.",
    },
  ];

  const allFeatures = [
    {
      icon: Utensils,
      title: "Smart Food Database",
      description:
        "Access our extensive global cuisine database with automatic CSV matching and LLM fallback for accurate nutrition data.",
    },
    {
      icon: LineChart,
      title: "Mifflin-St Jeor Goals",
      description:
        "Personalized daily calorie and macro targets calculated using BMR, activity level, and your specific health goals.",
    },
    {
      icon: Clock,
      title: "Intermittent Fasting",
      description:
        "Built-in fasting timer to track your eating windows with circadian alignment scoring for optimal gut health.",
    },
    {
      icon: Pill,
      title: "Medication Reminders",
      description:
        "Never miss a dose with customizable medication tracking, dosage management, and timely reminders.",
    },
    {
      icon: Target,
      title: "Weight Management",
      description:
        "Set target weight goals with smart calorie deficits automatically calculated based on your BMI and preferences.",
    },
    {
      icon: Moon,
      title: "Sleep & Recovery",
      description:
        "Track sleep duration, quality, and recovery metrics including HRV and resting heart rate for complete wellness.",
    },
    {
      icon: LineChart,
      title: "Glucose Spike Predictor",
      description:
        "Per-meal blood glucose spike predictions with confidence scores, time-to-peak, and recovery time estimates.",
    },
    {
      icon: Shield,
      title: "Metabolic Stability",
      description:
        "Monitor SpO₂, breathing rate, and skin temperature variability for comprehensive metabolic health insights.",
    },
  ];

  const integrations = [
    {
      name: "Fitbit",
      status: "Available",
      description:
        "Sync your complete Fitbit data including steps, sleep, heart rate, HRV, SpO₂, breathing rate, and skin temperature.",
      features: [
        "Real-time activity sync",
        "Sleep tracking & stages",
        "Heart rate & HRV metrics",
        "Night-time vitals (SpO₂, breathing)",
        "Active Zone Minutes",
      ],
      icon: Watch,
    },
    {
      name: "Apple Health",
      status: "Coming Soon",
      description:
        "Connect Apple Health to import health data from your iPhone and Apple Watch for comprehensive tracking.",
      features: [
        "Activity & workouts",
        "Sleep analysis",
        "Heart rate data",
        "Nutrition data sync",
        "Health records",
      ],
      icon: Smartphone,
    },
    {
      name: "Google Health Connect",
      status: "Coming Soon",
      description:
        "Connect Google Health Connect to sync health and fitness data from Android devices and compatible apps.",
      features: [
        "Activity & exercise data",
        "Sleep tracking",
        "Heart rate & vitals",
        "Nutrition data sync",
        "Cross-app data sharing",
      ],
      icon: Smartphone,
    },
    {
      name: "USDA Database",
      status: "Built-in",
      description:
        "Access comprehensive nutritional data from the USDA FoodData Central for accurate macro tracking.",
      features: [
        "8,000+ food items",
        "Complete macro data",
        "Micronutrient info",
        "Regular updates",
        "Scientific accuracy",
      ],
      icon: Database,
    },
  ];

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: "forever",
      description: "Perfect for getting started with basic health tracking.",
      features: [
        "AI meal scanning (10/month)",
        "Basic nutrition tracking",
        "Daily calorie goals",
        "Manual food logging",
        "7-day meal history",
      ],
      cta: "Get Started",
      popular: false,
    },
    {
      name: "Pro",
      price: "$9.99",
      period: "per month",
      description: "Complete health tracking for serious health optimization.",
      features: [
        "Unlimited AI meal scanning",
        "Bite Balance Score",
        "Glucose spike predictions",
        "Fitbit integration",
        "Health insights dashboard",
        "Intermittent fasting timer",
        "Medication reminders",
        "Unlimited meal history",
        "Priority support",
      ],
      cta: "Start Free Trial",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "per organization",
      description: "For healthcare providers and wellness organizations.",
      features: [
        "Everything in Pro",
        "API access",
        "Custom integrations",
        "White-label options",
        "HIPAA compliance",
        "Dedicated account manager",
        "Custom reporting",
        "SLA guarantee",
      ],
      cta: "Contact Sales",
      popular: false,
    },
  ];

  const faqs = [
    {
      question: "Can I cancel anytime?",
      answer:
        "Yes, you can cancel your subscription at any time. Your access will continue until the end of your billing period.",
    },
    {
      question: "Is there a free trial?",
      answer:
        "Yes, Pro plan includes a 14-day free trial. No credit card required to start.",
    },
    {
      question: "What payment methods do you accept?",
      answer:
        "We accept all major credit cards, Apple Pay, and Google Pay through our secure payment processor.",
    },
    {
      question: "Can I switch plans later?",
      answer:
        "Absolutely. You can upgrade or downgrade your plan at any time from your account settings.",
    },
  ];

  const values = [
    {
      title: "Science-First",
      description:
        "Every feature is backed by clinical research and validated health science. We use Mifflin-St Jeor equations, evidence-based gut health metrics, and peer-reviewed methodologies.",
    },
    {
      title: "Privacy-Focused",
      description:
        "Your health data is yours. We employ industry-leading encryption, HIPAA-compliant practices, and never sell your personal information to third parties.",
    },
    {
      title: "User-Centric",
      description:
        "We build for real people with real health goals. Every feature is designed to be intuitive, actionable, and genuinely helpful in your wellness journey.",
    },
    {
      title: "Continuous Innovation",
      description:
        "Health science evolves, and so do we. Our team constantly updates algorithms, adds new integrations, and improves accuracy based on the latest research.",
    },
  ];

  const timeline = [
    {
      year: "2024",
      event:
        "Bite Balance founded with a mission to democratize gut health tracking",
    },
    {
      year: "2024",
      event: "Launched AI-powered meal scanning with OpenAI Vision integration",
    },
    {
      year: "2024",
      event:
        "Released Bite Balance Score algorithm and glucose spike predictor",
    },
    {
      year: "2024",
      event: "Fitbit integration goes live for complete health tracking",
    },
    {
      year: "2025",
      event: "Expanding to Apple Health and more wearable integrations",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <LandingNav />

      {/* Hero Section */}
      <section id="hero" className="pb-16 pt-32 lg:pb-24 lg:pt-40">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
            {/* Left Content */}
            <div className="max-w-xl">
              <h1 className="mb-4 text-[3.5rem] font-bold leading-[1.05] tracking-tight text-black lg:text-[4.5rem]">
                Where{" "}
                <span className="relative inline-block">
                  AI
                  <HandDrawnCircle />
                </span>{" "}
                Meets{" "}
                <span className="relative inline-block">
                  Gut
                  <BrushHighlight />
                </span>{" "}
                Health
              </h1>
              <p className="mb-6 h-7 text-xl font-medium text-green-600">
                {taglineText}
                <span className="ml-1 inline-block h-5 w-0.5 animate-pulse bg-green-600" />
              </p>
              <p className="mb-8 text-lg leading-relaxed text-black/70">
                AI-powered meal scanning, Fitbit insights, blood glucose spike
                predictions with confidence scores, and a personalized Bite
                Balance Score to make smarter daily choices. Track progress and
                improve metabolic health over time.
              </p>
              <Button
                onClick={() => {
                  const el = document.getElementById("about");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                size="lg"
                className="rounded-full bg-black px-8 py-6 text-base font-medium text-white hover:bg-black/90"
              >
                Get in Touch
              </Button>
            </div>

            {/* Right Image Collage */}
            <div className="relative h-[500px] lg:h-[600px]">
              <div className="absolute left-0 top-0 h-[55%] w-[60%] overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=600&h=400&fit=crop"
                  alt="Healthy meal preparation"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="absolute right-0 top-4 h-[35%] w-[45%] overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop"
                  alt="Fitness tracking"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-4 top-4 rounded-lg bg-white px-3 py-1.5 shadow-md">
                  <span className="text-xs font-medium text-black">
                    AI-Powered Scanning
                  </span>
                </div>
              </div>
              <div className="absolute bottom-0 left-[15%] h-[45%] w-[50%] overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1505576399279-565b52d4ac71?w=500&h=400&fit=crop"
                  alt="Fresh healthy food"
                  className="h-full w-full object-cover"
                />
                <div className="absolute bottom-4 right-4 rounded-lg bg-white px-3 py-1.5 shadow-md">
                  <span className="text-xs font-medium text-black">
                    Gut Health Score
                  </span>
                </div>
              </div>
              <div className="absolute bottom-8 right-0 h-[35%] w-[40%] overflow-hidden rounded-2xl shadow-lg">
                <img
                  src="https://images.unsplash.com/photo-1434494878577-86c23bcb06b9?w=400&h=350&fit=crop"
                  alt="Smart watch fitness"
                  className="h-full w-full object-cover"
                />
                <div className="absolute left-4 top-4 rounded-lg bg-white px-3 py-1.5 shadow-md">
                  <span className="text-xs font-medium text-black">
                    Fitbit Integration
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Trust Badges */}
          <div className="mt-16 flex items-center justify-center gap-12 border-t border-black/10 pt-16">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black">
                <Check className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm font-semibold text-black">
                HIPAA Compliant
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black">
                <Check className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm font-semibold text-black">
                Science-Backed
              </span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-black">
                <Check className="h-5 w-5 text-white" />
              </div>
              <span className="text-sm font-semibold text-black">
                FDA Guidelines
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 1 - Gut Health Engine */}
      <section className="bg-neutral-50 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
                Bite Balance Score: Your Daily Health Pulse
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-black/70">
                Our proprietary algorithm combines meal glycemic load, body
                composition (BMI), and activity data (METs/Active Zone Minutes)
                into a single 0-100 Bite Balance Score. Know exactly where you
                stand—Excellent, Good, Caution, or Poor—at a glance.
              </p>
              <Button
                onClick={() => {
                  const el = document.getElementById("features");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full border-black px-6 text-black transition-colors hover:bg-black hover:text-white"
              >
                Learn more about scoring
              </Button>
            </div>
            <div className="relative">
              {/* Background decoration */}
              <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-green-100 to-emerald-50" />
              <div className="rounded-3xl border border-black/5 bg-white p-8 shadow-xl">
                {/* App mockup header */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                  </div>
                  <span className="text-xs font-medium text-black/40">
                    Bite Balance
                  </span>
                </div>

                {/* Score visualization */}
                <div className="mb-8 text-center">
                  <div className="relative mx-auto h-44 w-44">
                    <svg className="h-full w-full -rotate-90 transform">
                      <circle
                        cx="88"
                        cy="88"
                        r="75"
                        fill="none"
                        stroke="#f0f0f0"
                        strokeWidth="12"
                      />
                      <circle
                        cx="88"
                        cy="88"
                        r="75"
                        fill="none"
                        stroke="url(#scoreGradient)"
                        strokeWidth="12"
                        strokeLinecap="round"
                        strokeDasharray="471"
                        strokeDashoffset="103"
                      />
                      <defs>
                        <linearGradient
                          id="scoreGradient"
                          x1="0%"
                          y1="0%"
                          x2="100%"
                          y2="100%"
                        >
                          <stop offset="0%" stopColor="#22c55e" />
                          <stop offset="100%" stopColor="#000" />
                        </linearGradient>
                      </defs>
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-6xl font-bold text-black">78</span>
                      <span className="text-sm font-medium text-green-600">
                        Good
                      </span>
                    </div>
                  </div>
                </div>

                {/* Score factors */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-xl border border-black/5 bg-gradient-to-br from-neutral-50 to-white p-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-green-500" />
                      <p className="text-sm font-medium text-black/70">
                        Glycemic Load
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-green-600">
                      Low penalty
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-black/5 bg-gradient-to-br from-neutral-50 to-white p-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-black" />
                      <p className="text-sm font-medium text-black/70">
                        Activity Bonus
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-black">
                      +12 pts
                    </span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-black/5 bg-gradient-to-br from-neutral-50 to-white p-4">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-2 rounded-full bg-amber-500" />
                      <p className="text-sm font-medium text-black/70">
                        BMI Factor
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-amber-600">
                      0.95x
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 2 - AI Meal Scanning */}
      <section className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <img
                  src="https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=400&fit=crop"
                  alt="Healthy salad"
                  className="aspect-square w-full rounded-2xl object-cover shadow-lg"
                />
                <img
                  src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=400&h=400&fit=crop"
                  alt="Healthy bowl"
                  className="mt-8 aspect-square w-full rounded-2xl object-cover shadow-lg"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
                Empower Your Nutrition With AI Intelligence
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-black/70">
                Snap a photo and let our AI do the rest. Powered by OpenAI
                Vision, we identify foods, estimate portions, and calculate
                complete nutritional data including carbs, fiber, sugar,
                protein, fats, GI, and GL—all in seconds.
              </p>
              <Button
                onClick={() => {
                  const el = document.getElementById("features");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full border-black px-6 text-black transition-colors hover:bg-black hover:text-white"
              >
                Learn more
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 3 - Fitbit Integration */}
      <section className="bg-neutral-50 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
                Seamless Fitbit Integration for Complete Health Tracking
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-black/70">
                Connect your Fitbit to unlock powerful insights. Track steps,
                heart rate, sleep quality, HRV, and active minutes—all
                synchronized in real-time. Your fitness data combines with
                nutrition tracking to give you a complete picture of your
                metabolic health.
              </p>
              <div className="mb-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Real-time activity sync
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Sleep & recovery tracking
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Check className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Heart rate & HRV analysis
                  </span>
                </div>
              </div>
              <Button
                onClick={() => {
                  const el = document.getElementById("integrations");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full border-black px-6 text-black transition-colors hover:bg-black hover:text-white"
              >
                View all integrations
              </Button>
            </div>
            <div className="relative">
              <div className="rounded-3xl bg-white p-8 shadow-xl">
                <div className="mb-6 flex items-center gap-4">
                  <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[#00B0B9]">
                    <span className="text-xl font-bold text-white">f</span>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-black">
                      Fitbit Connected
                    </p>
                    <p className="text-sm text-black/50">
                      Last synced: Just now
                    </p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center justify-between rounded-xl bg-neutral-50 p-4">
                    <span className="text-black/70">Steps Today</span>
                    <span className="text-lg font-bold text-black">8,432</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-neutral-50 p-4">
                    <span className="text-black/70">Resting Heart Rate</span>
                    <span className="text-lg font-bold text-black">62 bpm</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-neutral-50 p-4">
                    <span className="text-black/70">Sleep Score</span>
                    <span className="text-lg font-bold text-black">85/100</span>
                  </div>
                  <div className="flex items-center justify-between rounded-xl bg-neutral-50 p-4">
                    <span className="text-black/70">Active Minutes</span>
                    <span className="text-lg font-bold text-black">42 min</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Showcase 4 - Glucose Spike Prediction */}
      <section className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
                Predict Blood Glucose Spikes Before They Happen
              </h2>
              <p className="mb-8 text-lg leading-relaxed text-black/70">
                Every meal you log gets an instant glucose spike
                prediction—categorized as High, Medium, or Low—along with a
                confidence score so you know how reliable the estimate is. Make
                smarter food choices by understanding how each meal impacts your
                blood sugar.
              </p>
              <div className="mb-8 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Per-meal spike level prediction
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Target className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Confidence scores for every prediction
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-black">
                    <Clock className="h-4 w-4 text-white" />
                  </div>
                  <span className="font-medium text-black">
                    Time-to-peak & recovery estimates
                  </span>
                </div>
              </div>
              <Button
                onClick={() => {
                  const el = document.getElementById("features");
                  el?.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                className="rounded-full border-black px-6 text-black transition-colors hover:bg-black hover:text-white"
              >
                Explore features
              </Button>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-[2.5rem] bg-gradient-to-br from-amber-50 to-orange-50" />
              <div className="rounded-3xl border border-black/5 bg-white p-8 shadow-xl">
                {/* Mockup header */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-3 w-3 rounded-full bg-red-400" />
                    <div className="h-3 w-3 rounded-full bg-yellow-400" />
                    <div className="h-3 w-3 rounded-full bg-green-400" />
                  </div>
                  <span className="text-xs font-medium text-black/40">
                    Glucose Predictor
                  </span>
                </div>

                {/* Meal predictions */}
                <div className="space-y-4">
                  <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-red-500" />
                        <span className="font-semibold text-black">
                          Breakfast — Pancakes
                        </span>
                      </div>
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600">
                        HIGH
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-black/60">
                        Predicted spike:{" "}
                        <span className="font-semibold text-black">
                          +62 mg/dL
                        </span>
                      </span>
                      <span className="text-black/50">
                        Confidence:{" "}
                        <span className="font-semibold text-black">87%</span>
                      </span>
                    </div>
                    <div className="mt-2 flex gap-4 text-xs text-black/40">
                      <span>Peak: ~35 min</span>
                      <span>Recovery: ~90 min</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-amber-500" />
                        <span className="font-semibold text-black">
                          Lunch — Grilled Chicken Wrap
                        </span>
                      </div>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-600">
                        MEDIUM
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-black/60">
                        Predicted spike:{" "}
                        <span className="font-semibold text-black">
                          +34 mg/dL
                        </span>
                      </span>
                      <span className="text-black/50">
                        Confidence:{" "}
                        <span className="font-semibold text-black">92%</span>
                      </span>
                    </div>
                    <div className="mt-2 flex gap-4 text-xs text-black/40">
                      <span>Peak: ~25 min</span>
                      <span>Recovery: ~55 min</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-green-200 bg-green-50 p-4">
                    <div className="mb-2 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-green-500" />
                        <span className="font-semibold text-black">
                          Dinner — Salmon & Veggies
                        </span>
                      </div>
                      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-bold text-green-600">
                        LOW
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-black/60">
                        Predicted spike:{" "}
                        <span className="font-semibold text-black">
                          +12 mg/dL
                        </span>
                      </span>
                      <span className="text-black/50">
                        Confidence:{" "}
                        <span className="font-semibold text-black">95%</span>
                      </span>
                    </div>
                    <div className="mt-2 flex gap-4 text-xs text-black/40">
                      <span>Peak: ~15 min</span>
                      <span>Recovery: ~30 min</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="bg-neutral-50 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-20 text-center">
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
              Features built for complete health tracking
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-black/60">
              From AI-powered meal recognition and glucose spike predictions to
              comprehensive fitness integration, Bite Balance provides
              everything you need to optimize your metabolic wellness.
            </p>
          </div>

          {/* Core Features */}
          <h3 className="mb-8 text-center text-sm font-semibold uppercase tracking-wider text-black/40">
            Core Features
          </h3>
          <div className="mb-16 grid gap-12 md:grid-cols-2">
            {coreFeatures.map((feature, index) => (
              <div key={index} className="flex gap-6">
                <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-black">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h4 className="mb-2 text-xl font-semibold text-black">
                    {feature.title}
                  </h4>
                  <p className="leading-relaxed text-black/60">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* All Features Grid */}
          <h3 className="mb-8 text-center text-sm font-semibold uppercase tracking-wider text-black/40">
            All Features
          </h3>
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
            {allFeatures.map((feature, index) => (
              <div
                key={index}
                className="rounded-2xl border border-black/10 bg-white p-6 transition-colors hover:border-black/30"
              >
                <feature.icon className="mb-4 h-8 w-8 text-black" />
                <h4 className="mb-2 text-lg font-semibold text-black">
                  {feature.title}
                </h4>
                <p className="text-sm leading-relaxed text-black/60">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integrations" className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
              Integrations that power your health
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-black/60">
              Connect your favorite devices and services to get a complete
              picture of your health. Bite Balance integrates seamlessly with
              the tools you already use.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {integrations.map((integration, index) => (
              <div
                key={index}
                className="rounded-3xl border border-black/10 p-8"
              >
                <div className="mb-6 flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-black">
                      <integration.icon className="h-7 w-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-black">
                        {integration.name}
                      </h3>
                      <span
                        className={`text-sm ${integration.status === "Available" ? "text-green-600" : integration.status === "Built-in" ? "text-black/60" : "text-black/40"}`}
                      >
                        {integration.status}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mb-6 text-black/60">{integration.description}</p>
                <ul className="space-y-2">
                  {integration.features.map((feature, i) => (
                    <li
                      key={i}
                      className="flex items-center gap-3 text-sm text-black/70"
                    >
                      <Check className="h-4 w-4 text-black" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="bg-neutral-50 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
              Simple, transparent pricing
            </h2>
            <p className="mx-auto max-w-2xl text-lg text-black/60">
              Choose the plan that fits your health journey. Start free, upgrade
              when you're ready.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="mb-20 grid gap-8 md:grid-cols-3">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative rounded-3xl border-2 p-8 ${plan.popular ? "border-black bg-black text-white" : "border-black/10 bg-white"}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-1 text-sm font-semibold text-black">
                    Most Popular
                  </div>
                )}
                <div className="mb-6">
                  <h3
                    className={`mb-2 text-lg font-semibold ${plan.popular ? "text-white" : "text-black"}`}
                  >
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline gap-1">
                    <span
                      className={`text-4xl font-bold ${plan.popular ? "text-white" : "text-black"}`}
                    >
                      {plan.price}
                    </span>
                    <span
                      className={
                        plan.popular ? "text-white/60" : "text-black/40"
                      }
                    >
                      {plan.period}
                    </span>
                  </div>
                  <p
                    className={`mt-3 text-sm ${plan.popular ? "text-white/70" : "text-black/60"}`}
                  >
                    {plan.description}
                  </p>
                </div>
                <ul className="mb-8 space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check
                        className={`h-5 w-5 flex-shrink-0 ${plan.popular ? "text-white" : "text-black"}`}
                      />
                      <span
                        className={`text-sm ${plan.popular ? "text-white/80" : "text-black/70"}`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate("/login")}
                  className={`w-full rounded-full py-6 font-medium ${plan.popular ? "bg-white text-black hover:bg-white/90" : "bg-black text-white hover:bg-black/90"}`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>

          {/* FAQs */}
          <div className="mx-auto max-w-3xl">
            <h3 className="mb-12 text-center text-2xl font-bold text-black">
              Frequently Asked Questions
            </h3>
            <div className="space-y-8">
              {faqs.map((faq, index) => (
                <div key={index} className="border-b border-black/10 pb-8">
                  <h4 className="mb-2 text-lg font-semibold text-black">
                    {faq.question}
                  </h4>
                  <p className="text-black/60">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
              Our mission is to make gut health accessible to everyone
            </h2>
            <p className="mx-auto max-w-3xl text-lg leading-relaxed text-black/60">
              We believe that understanding your gut health shouldn't require
              expensive tests or complex medical knowledge. Bite Balance brings
              clinical-grade health insights to your fingertips.
            </p>
          </div>

          {/* Story & Timeline */}
          <div className="mb-20 grid gap-16 lg:grid-cols-2">
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-black/40">
                Our Story
              </h3>
              <h4 className="mb-6 text-2xl font-bold text-black lg:text-3xl">
                Born from a simple idea
              </h4>
              <div className="space-y-4 leading-relaxed text-black/60">
                <p>
                  Bite Balance started with a simple observation: gut health is
                  crucial to overall wellness, but tracking it effectively has
                  always been complicated and expensive.
                </p>
                <p>
                  We set out to change that by combining the latest advances in
                  AI, wearable technology, and nutritional science to create a
                  tool that makes understanding your gut health as simple as
                  taking a photo of your meal.
                </p>
                <p>
                  Today, Bite Balance helps users optimize their metabolic
                  health through personalized insights, smart tracking, and
                  science-backed recommendations.
                </p>
              </div>
            </div>
            <div>
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-black/40">
                Timeline
              </h3>
              <div className="space-y-6">
                {timeline.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <span className="w-12 flex-shrink-0 text-sm font-semibold text-black/40">
                      {item.year}
                    </span>
                    <p className="text-black/70">{item.event}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Values */}
          <div className="rounded-3xl bg-neutral-50 p-8 lg:p-12">
            <div className="mb-12 text-center">
              <h3 className="mb-4 text-sm font-semibold uppercase tracking-wider text-black/40">
                Our Values
              </h3>
              <h4 className="text-2xl font-bold text-black lg:text-3xl">
                What we stand for
              </h4>
            </div>
            <div className="grid gap-12 md:grid-cols-2">
              {values.map((value, index) => (
                <div key={index}>
                  <h5 className="mb-3 text-xl font-semibold text-black">
                    {value.title}
                  </h5>
                  <p className="leading-relaxed text-black/60">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-32">
        <div className="mx-auto max-w-4xl px-6 text-center lg:px-8">
          <h2 className="mb-6 text-3xl font-bold tracking-tight text-black lg:text-5xl">
            Ready to Transform Your Health?
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-lg text-black/70">
            Be among the first to experience Bite Balance. Join our waitlist and
            we'll notify you when we launch.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full rounded-full border-0 bg-neutral-100 px-6 py-4 text-black placeholder:text-black/40 focus:outline-none focus:ring-2 focus:ring-black sm:w-80"
            />
            <Button
              onClick={() => {
                const el = document.getElementById("about");
                el?.scrollIntoView({ behavior: "smooth" });
              }}
              size="lg"
              className="w-full rounded-full bg-black px-8 py-6 font-medium text-white hover:bg-black/90 sm:w-auto"
            >
              Get in Touch
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default LandingPage;
